import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { X, Minus, Plus, Trash2, CheckCircle2, MessageCircle } from "lucide-react";
import { useCart, brl } from "../context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WA = process.env.REACT_APP_WHATSAPP_NUMBER;

export const CartDrawer = () => {
  const { items, setQty, clear, total, open, setOpen, checkoutTick, coupon, setCoupon } = useCart();
  const [step, setStep] = useState("cart");
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "" });
  const [order, setOrder] = useState(null);
  const [summary, setSummary] = useState("");
  const [sending, setSending] = useState(false);
  const [delivery, setDelivery] = useState({ method: "pickup", cep: "" });
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    if (open && !cfg) {
      fetch(`${API}/content`).then((r) => r.json()).then(setCfg).catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    if (open && checkoutTick > 0) setStep("checkout");
  }, [checkoutTick, open]);

  const discount = coupon?.discount || 0;
  const [couponInput, setCouponInput] = useState("");
  const [quotes, setQuotes] = useState(null);
  const [selectedQuote, setSelectedQuote] = useState(null);

  useEffect(() => {
    if (delivery.method !== "correios") {
      setQuotes(null);
      setSelectedQuote(null);
      return;
    }
    const cep = delivery.cep.replace(/\D/g, "");
    if (cep.length !== 8) {
      setQuotes(null);
      setSelectedQuote(null);
      return;
    }
    let cancelled = false;
    setQuotes({ loading: true });
    axios.post(`${API}/freight/quote`, { destination_cep: cep, items: items.map((i) => ({ slug: i.slug, qty: i.qty })) })
      .then((r) => { if (!cancelled) { setQuotes(r.data); setSelectedQuote(r.data.options?.[0] || null); } })
      .catch(() => { if (!cancelled) setQuotes({ available: false, note: cfg?.correios_note }); });
    return () => { cancelled = true; };
  }, [delivery.method, delivery.cep]);

  const fee = delivery.method === "pickup" ? 0 : delivery.method === "motoboy" ? cfg?.motoboy_fee ?? null : selectedQuote?.price ?? cfg?.correios_fee ?? null;
  const grand = Math.max(0, total - discount) + (fee ?? 0);

  const applyCoupon = async () => {
    if (!couponInput.trim()) return;
    try {
      const { data } = await axios.post(`${API}/coupons/validate`, {
        code: couponInput.trim(),
        subtotal: total,
        items: items.map((i) => ({ product_id: i.slug, price: i.price, qty: i.qty })),
      });
      setCoupon({ code: data.code, discount: data.discount });
      setCouponInput("");
    } catch (err) {
      alert(err.response?.data?.detail || "Cupom inválido.");
    }
  };

  const close = () => {
    setOpen(false);
    if (step === "done") setStep("cart");
  };

  const submit = async (e) => {
    e.preventDefault();
    const cepDigits = delivery.cep.replace(/\D/g, "");
    if (delivery.method === "motoboy") {
      if (cepDigits.length !== 8) return alert("Informe um CEP válido para entrega por motoboy em Londrina.");
      if (!["860", "861"].includes(cepDigits.slice(0, 3))) return alert("Esse CEP está fora da área de Londrina. Escolha o envio pelos Correios.");
    }
    if (delivery.method === "correios" && cepDigits.length !== 8) return alert("Informe um CEP válido para envio pelos Correios.");
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/orders`, {
        customer: { ...form, email: form.email.toLowerCase().trim() },
        items: items.map((i) => ({ product_id: i.slug, name: i.name + (i.variant_name ? ` — ${i.variant_name}` : ""), variant: i.variant_name, price: i.price, qty: i.qty })),
        total: grand,
        delivery: { method: delivery.method, cep: cepDigits || null, fee },
        coupon: coupon ? { code: coupon.code } : null,
      });
      const methodLabel = { motoboy: "Motoboy (Londrina)", correios: "Correios", pickup: "Retirada na loja" }[delivery.method];
      const feeText = delivery.method === "pickup" ? "Grátis" : fee == null ? "frete a combinar" : `frete ${brl(fee)}`;
      const lines = items.map((i) => `• ${i.qty}x ${i.name} — ${brl(i.price * i.qty)}`).join("\n");
      const photoLines = items.filter((i) => i.image).map((i) => `${i.name}: ${i.image}`).join("\n");
      const couponLine = coupon ? `\nCupom ${coupon.code}: −${brl(discount)}` : "";
      setSummary(
        `Olá! Acabei de fazer o pedido ${data.code} no site da leve essenza:\n\n${lines}\n\nSubtotal: ${brl(total)}${couponLine}\nRecebimento: ${methodLabel} (${feeText})\nTotal: ${brl(data.total)}\nNome: ${form.name}${photoLines ? `\n\nFotos dos produtos:\n${photoLines}` : ""}\n\nPodem confirmar pagamento e entrega?`
      );
      setOrder(data);
      setStep("done");
      clear();
      setCoupon(null);
    } catch (err) {
      alert(err.response?.data?.detail || "Não foi possível finalizar o pedido. Tente novamente.");
    }
    setSending(false);
  };

  const inputCls = "w-full bg-white/70 border border-military/20 rounded-lg px-4 py-3 text-sm text-military-dark placeholder:text-military-dark/40 focus:outline-none focus:border-military transition-colors";

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            data-testid="cart-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
            className="fixed inset-0 z-[60] bg-military-dark/40 backdrop-blur-sm"
          />
          <motion.aside
            data-testid="cart-drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.5, ease: [0.76, 0, 0.24, 1] }}
            className="fixed top-0 right-0 bottom-0 z-[70] w-full max-w-md bg-offwhite/90 backdrop-blur-xl border-l border-white/40 shadow-2xl flex flex-col"
          >
            <div className="flex items-center justify-between px-8 py-6 border-b border-military/10">
              <h3 className="font-serif text-2xl text-military-dark">
                {step === "cart" && "Sua sacola"}
                {step === "checkout" && "Finalizar pedido"}
                {step === "done" && "Pedido confirmado"}
              </h3>
              <button data-testid="cart-close-button" onClick={close} className="p-2 rounded-full hover:bg-baby transition-colors">
                <X size={20} className="text-military-dark" />
              </button>
            </div>

            {step === "cart" && (
              <>
                <div className="flex-1 overflow-y-auto px-8 py-6 space-y-6" data-testid="cart-items">
                  {items.length === 0 && (
                    <p className="font-serif italic text-xl text-military-dark/50 text-center mt-16">Sua sacola está leve demais.</p>
                  )}
                  {items.map((i) => {
                    const itemKey = i.key || i.slug;
                    return (
                    <div key={itemKey} data-testid={`cart-item-${itemKey}`} className="flex gap-4 border-b border-military/10 pb-6">
                      <img src={i.image} alt={i.name} className="w-20 h-20 object-cover rounded-lg" />
                      <div className="flex-1">
                        <div className="flex justify-between gap-2">
                          <p className="font-serif text-lg text-military-dark leading-tight">{i.name}</p>
                          <button data-testid={`remove-${itemKey}`} onClick={() => setQty(itemKey, 0)} className="text-military-dark/40 hover:text-destructive transition-colors">
                            <Trash2 size={16} />
                          </button>
                        </div>
                        {i.variant_name && <p className="text-[10px] uppercase tracking-[0.15em] text-military font-semibold mt-0.5" data-testid={`variant-label-${itemKey}`}>Sabor: {i.variant_name}</p>}
                        <p className="text-sm text-military-dark/60 mt-1">{brl(i.price)}</p>
                        <div className="flex items-center gap-3 mt-3">
                          <button data-testid={`qty-minus-${itemKey}`} onClick={() => setQty(itemKey, i.qty - 1)} className="w-7 h-7 rounded-full border border-military/20 flex items-center justify-center hover:bg-baby transition-colors">
                            <Minus size={12} />
                          </button>
                          <span className="text-sm font-semibold w-4 text-center" data-testid={`qty-${itemKey}`}>{i.qty}</span>
                          <button data-testid={`qty-plus-${itemKey}`} onClick={() => setQty(itemKey, Math.min(i.stock ?? 99, i.qty + 1))} className="w-7 h-7 rounded-full border border-military/20 flex items-center justify-center hover:bg-baby transition-colors">
                            <Plus size={12} />
                          </button>
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
                <div className="px-8 py-6 border-t border-military/10">
                  {coupon ? (
                    <div className="flex items-center justify-between mb-4 bg-baby/60 border border-military/15 rounded-lg px-4 py-2.5" data-testid="coupon-applied">
                      <span className="text-xs font-semibold text-military-dark font-mono">{coupon.code} <span className="font-sans text-military-dark/60">(−{brl(coupon.discount)})</span></span>
                      <button data-testid="coupon-remove" onClick={() => setCoupon(null)} className="text-xs text-military-dark/50 hover:text-red-700">remover</button>
                    </div>
                  ) : (
                    <div className="flex gap-2 mb-4">
                      <input
                        data-testid="coupon-input"
                        placeholder="Cupom de desconto"
                        value={couponInput}
                        onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                        className="flex-1 bg-white/70 border border-military/20 rounded-lg px-4 py-2.5 text-sm font-mono uppercase placeholder:normal-case placeholder:font-sans focus:outline-none focus:border-military"
                      />
                      <button data-testid="coupon-apply" onClick={applyCoupon} className="rounded-lg bg-military text-offwhite px-4 text-xs uppercase tracking-widest font-semibold hover:bg-military-dark transition-colors">Aplicar</button>
                    </div>
                  )}
                  <div className="flex justify-between mb-1 text-sm text-military-dark/60">
                    <span>Subtotal</span>
                    <span data-testid="cart-subtotal">{brl(total)}</span>
                  </div>
                  {coupon && (
                    <div className="flex justify-between mb-4 text-sm font-semibold text-military" data-testid="cart-discount-line">
                      <span>Cupom {coupon.code}</span>
                      <span>−{brl(discount)}</span>
                    </div>
                  )}
                  <button
                    data-testid="go-checkout-button"
                    disabled={items.length === 0}
                    onClick={() => setStep("checkout")}
                    className="w-full rounded-full bg-military-dark text-offwhite py-4 text-sm uppercase tracking-widest font-semibold hover:bg-military transition-colors duration-300 disabled:opacity-30"
                  >
                    Ir para o checkout
                  </button>
                </div>
              </>
            )}

            {step === "checkout" && (
              <form onSubmit={submit} className="flex-1 overflow-y-auto px-8 py-6 flex flex-col" data-testid="checkout-form">
                <div className="space-y-4 flex-1">
                  <input data-testid="checkout-name" required placeholder="Nome completo" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
                  <input data-testid="checkout-email" required type="email" placeholder="E-mail" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
                  <input data-testid="checkout-phone" required placeholder="WhatsApp" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} />
                  <textarea data-testid="checkout-address" required placeholder="Endereço de entrega" rows={3} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inputCls} />
                  <div data-testid="delivery-options">
                    <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-military-dark/50 mb-2 mt-2">Recebimento</p>
                    <div className="space-y-2">
                      {[
                        { id: "motoboy", label: "Motoboy — Londrina", desc: cfg?.motoboy_fee != null ? `+ ${brl(cfg.motoboy_fee)}` : "frete a combinar" },
                        { id: "correios", label: "Correios — outras cidades", desc: cfg?.correios_fee != null ? `+ ${brl(cfg.correios_fee)}` : "frete informado após o pedido" },
                        { id: "pickup", label: "Retirar na loja", desc: "Grátis" },
                      ].map((opt) => (
                        <button
                          key={opt.id}
                          type="button"
                          data-testid={`delivery-${opt.id}`}
                          onClick={() => setDelivery({ ...delivery, method: opt.id })}
                          className={`w-full flex items-center justify-between rounded-lg border px-4 py-3 text-left transition-colors ${delivery.method === opt.id ? "border-military bg-baby/60" : "border-military/20 bg-white/70 hover:border-military/40"}`}
                        >
                          <span className="text-sm font-semibold text-military-dark">{opt.label}</span>
                          <span className="text-xs text-military-dark/60">{opt.desc}</span>
                        </button>
                      ))}
                    </div>
                    {delivery.method !== "pickup" && (
                      <input
                        data-testid="delivery-cep-input"
                        required
                        inputMode="numeric"
                        placeholder="Seu CEP (somente números)"
                        value={delivery.cep}
                        onChange={(e) => setDelivery({ ...delivery, cep: e.target.value })}
                        className={inputCls + " mt-3"}
                      />
                    )}
                    {delivery.method === "motoboy" && <p className="text-xs text-military-dark/50 mt-2">Entrega por motoboy válida para CEPs de Londrina (860xx e 861xx).</p>}
                    {delivery.method === "correios" && (
                      <div className="mt-3" data-testid="correios-quotes">
                        {quotes?.loading && <p className="text-xs text-military-dark/50">Calculando frete pelos Correios...</p>}
                        {quotes?.available && (
                          <div className="space-y-2">
                            {quotes.options.map((o, i) => (
                              <button
                                key={i}
                                type="button"
                                data-testid={`quote-option-${i}`}
                                onClick={() => setSelectedQuote(o)}
                                className={`w-full flex items-center justify-between rounded-lg border px-4 py-2.5 text-left transition-colors ${selectedQuote === o ? "border-military bg-baby/60" : "border-military/20 bg-white/70"}`}
                              >
                                <span className="text-sm font-semibold text-military-dark">{o.service}{o.delivery_days ? ` · até ${o.delivery_days} dias úteis` : ""}</span>
                                <span className="text-sm font-semibold text-military-dark">{brl(o.price)}</span>
                              </button>
                            ))}
                          </div>
                        )}
                        {quotes && !quotes.loading && !quotes.available && (
                          <p className="text-xs text-military-dark/50 leading-relaxed">{quotes.note || cfg?.correios_note || "Frete pelos Correios informado após o pedido."}</p>
                        )}
                        {!quotes && <p className="text-xs text-military-dark/50">{cfg?.correios_note || "Frete pelos Correios informado após o pedido."}</p>}
                      </div>
                    )}
                    {delivery.method === "pickup" && (
                      <div className="mt-3 bg-baby/60 border border-military/15 rounded-lg p-4 text-xs text-military-dark/70 leading-relaxed" data-testid="pickup-info">
                        <p className="font-semibold text-military-dark mb-1">Retirada na loja — grátis</p>
                        <p>{cfg?.pickup_address}</p>
                        <p>{cfg?.pickup_hours}</p>
                        <p className="mt-1">{cfg?.pickup_instructions}</p>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-military-dark/50 leading-relaxed">Pagamento simulado para demonstração — nenhuma cobrança é feita.</p>
                </div>
                <div className="pt-6">
                  <div className="space-y-1.5 mb-5">
                    <div className="flex justify-between text-sm text-military-dark/60">
                      <span>Subtotal</span><span>{brl(total)}</span>
                    </div>
                    {coupon && (
                      <div className="flex justify-between text-sm font-semibold text-military" data-testid="checkout-discount-line">
                        <span>Cupom {coupon.code}</span><span>−{brl(discount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm text-military-dark/60" data-testid="delivery-fee-line">
                      <span>Frete</span>
                      <span>{delivery.method === "pickup" ? "Grátis" : fee == null ? "a combinar" : brl(fee)}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-military/10">
                      <span className="text-xs uppercase tracking-[0.25em] text-military-dark/60 font-semibold">Total</span>
                      <span data-testid="checkout-total" className="font-serif text-2xl text-military-dark">{brl(grand)}</span>
                    </div>
                  </div>
                  <button
                    data-testid="place-order-button"
                    type="submit"
                    disabled={sending}
                    className="w-full rounded-full bg-military-dark text-offwhite py-4 text-sm uppercase tracking-widest font-semibold hover:bg-baby hover:text-military-dark transition-colors duration-300 disabled:opacity-50"
                  >
                    {sending ? "Enviando..." : "Confirmar pedido"}
                  </button>
                </div>
              </form>
            )}

            {step === "done" && order && (
              <div className="flex-1 px-8 py-12 flex flex-col items-center justify-center text-center" data-testid="order-confirmation">
                <CheckCircle2 size={56} className="text-military mb-6" />
                <p className="font-serif text-3xl text-military-dark mb-2">Obrigado, atleta.</p>
                <p className="text-sm text-military-dark/60 mb-6">Pedido <span data-testid="order-code" className="font-semibold text-military-dark">{order.code}</span> registrado — total de {brl(order.total)}.</p>
                <p className="text-sm text-military-dark/60 mb-8 max-w-xs">Nossa equipe entra em contato pelo WhatsApp para combinar pagamento e entrega.</p>
                <a
                  data-testid="order-whatsapp-link"
                  href={`https://wa.me/${WA}?text=${encodeURIComponent(summary)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-full bg-military text-offwhite px-6 py-3 text-xs uppercase tracking-widest font-semibold hover:bg-military-dark transition-colors flex items-center gap-2"
                >
                  <MessageCircle size={14} /> Falar no WhatsApp
                </a>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
